import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { userApi, UserUsage } from '../api/user'

export default function UserProfilePage() {
  const { user, logout } = useAuth()
  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [usage, setUsage] = useState<UserUsage | null>(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    async function fetchUsage() {
      try {
        const response = await userApi.getUsage()
        setUsage(response.data)
      } catch {
        // ignore
      } finally {
        setUsageLoading(false)
      }
    }
    fetchUsage()
  }, [])

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    const updateData: any = {}
    if (name !== user?.name) updateData.name = name
    if (email !== user?.email) updateData.email = email
    if (password) updateData.password = password

    if (Object.keys(updateData).length === 0) {
      setIsLoading(false)
      return
    }

    try {
      await userApi.updateMe(updateData)
      setSuccess('Profile updated successfully')
      setPassword('')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Update failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete your account? This cannot be undone.')) return

    try {
      await userApi.deleteMe()
      logout()
      navigate('/')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed')
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Profile Settings</h1>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4 mb-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* AI Usage Section */}
      <div className="bg-white shadow-sm border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Usage</h2>
        {usageLoading ? (
          <div className="text-sm text-gray-500">Loading usage...</div>
        ) : usage ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Plan:</span>
              <span className="px-2 py-1 rounded text-xs font-medium capitalize bg-blue-100 text-blue-700">
                {usage.plan}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Daily</span>
                  <span className="text-gray-900 font-medium">
                    {usage.used_daily.toLocaleString()} / {usage.daily_limit.toLocaleString()} AI units
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (usage.used_daily / usage.daily_limit) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {usage.remaining_daily.toLocaleString()} remaining today
                </p>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Monthly</span>
                  <span className="text-gray-900 font-medium">
                    {usage.used_monthly.toLocaleString()} / {usage.monthly_limit.toLocaleString()} AI units
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (usage.used_monthly / usage.monthly_limit) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {usage.remaining_monthly.toLocaleString()} remaining this month
                </p>
              </div>
            </div>

            {/* Token breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Token Breakdown (Today)</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-green-50 rounded p-2">
                  <div className="text-xs text-green-700">Cache Hit</div>
                  <div className="text-sm font-medium text-green-900">{usage.cache_hit_daily.toLocaleString()}</div>
                </div>
                <div className="bg-yellow-50 rounded p-2">
                  <div className="text-xs text-yellow-700">Cache Miss</div>
                  <div className="text-sm font-medium text-yellow-900">{usage.cache_miss_daily.toLocaleString()}</div>
                </div>
                <div className="bg-red-50 rounded p-2">
                  <div className="text-xs text-red-700">Output</div>
                  <div className="text-sm font-medium text-red-900">{usage.output_daily.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-400">
              1 AI unit = 1000 tokens. Limits reset daily at midnight UTC and monthly on the 1st.
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Unable to load usage data.</div>
        )}
      </div>

      <form onSubmit={handleUpdate} className="space-y-6 bg-white shadow-sm border rounded-lg p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">New Password (leave blank to keep current)</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 8 chars, letters, numbers, special"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div className="flex justify-between">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>

          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete Account
          </button>
        </div>
      </form>
    </div>
  )
}
